import React, { useState, useEffect, useRef } from 'react';
import { FiList, FiGrid, FiFilter, FiX, FiDownload, FiUpload, FiChevronDown, FiCheck, FiSearch, FiSquare } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { Bounce } from 'react-toastify';
import { toast } from '../../utils/customToast';
import ExportModal from '../modals/ExportModal';
import ImportModal from '../modals/ImportModal';
// useMessageSettings est appelé dans le parent (Messages.jsx) et transmis en props
// pour éviter une instance dupliquée et un double fetch
import { apiFetch } from '../../utils/api';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import KeyboardShortcutBadge from '../shared/KeyboardShortcutBadge';
import { getSearchShortcut } from '../../utils/keyboardUtils';

const MESSAGE_TYPES = [
    'Rappel mensuel',
    'Rappel hebdomadaire',
    'Rappel quotidien',
    'Notification',
    'Information',
    'Alerte',
    'Mise à jour'
];

const ROLES = ['CIO Office', 'PMO', 'Système'];

const COLUMN_OPTIONS = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
];

const VIEW_MODES = [
    { value: 'inline', label: 'En ligne', icon: FiList, description: 'Favoris en premier' },
    { value: 'tabs', label: 'Onglets', icon: FiGrid, description: 'Onglets séparés' }
];

const MessageSearch = ({ onSearch, columns, setColumns, onImportSuccess, onExportDone, onSearchClick, messageTypes, messageRoles }) => {
    const [type, setType] = useState('Tous');
    const [role, setRole] = useState('Tous');
    const [priority, setPriority] = useState('Tous');
    const [person, setPerson] = useState('Tous');
    const [members, setMembers] = useState([]);
    const [pendingColumns, setPendingColumns] = useState(columns);
    const [showViewMenu, setShowViewMenu] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [showTypeMenu, setShowTypeMenu] = useState(false);
    const [showRoleMenu, setShowRoleMenu] = useState(false);
    const [showPriorityMenu, setShowPriorityMenu] = useState(false);
    const [showPersonMenu, setShowPersonMenu] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    // States for combobox autocomplete
    const [typeInput, setTypeInput] = useState('');
    const [roleInput, setRoleInput] = useState('');
    const [priorityInput, setPriorityInput] = useState('');
    const [personInput, setPersonInput] = useState('');
    const { user, setFavoritesViewMode, toggleColoredBorder, hasTenantRole, activeTenantId, getAuthHeaders } = useAuth();
    const { handleApiError } = useApiErrorHandler();

    const normalizedTypes = (messageTypes && messageTypes.length)
        ? messageTypes.map(t => typeof t === 'string' ? { name: t } : { name: t.name })
        : MESSAGE_TYPES.map(n => ({ name: n }));

    const types = normalizedTypes.map(t => t.name);
    const roles = (messageRoles && messageRoles.length) ? messageRoles : ROLES;
    const canManage = hasTenantRole ? hasTenantRole(activeTenantId, ['owner', 'admin']) : false;

    // Récupération des membres du tenant
    useEffect(() => {
        if (!activeTenantId) return;

        const controller = new AbortController();

        apiFetch(
            `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/members`,
            { headers: getAuthHeaders(), signal: controller.signal },
            handleApiError
        )
            .then(data => setMembers(data || []))
            .catch(err => {
                if (err.name !== 'AbortError') {
                    console.error('Error fetching members:', err);
                    setMembers([]);
                }
            });

        return () => controller.abort();
    }, [activeTenantId, getAuthHeaders, handleApiError]);
    const menuRef = useRef(null);
    const filtersRef = useRef(null);
    const typeMenuRef = useRef(null);
    const roleMenuRef = useRef(null);
    const priorityMenuRef = useRef(null);
    const personMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowViewMenu(false);
            }
            if (filtersRef.current && !filtersRef.current.contains(event.target)) {
                setShowFilters(false);
            }
            if (typeMenuRef.current && !typeMenuRef.current.contains(event.target)) {
                setShowTypeMenu(false);
            }
            if (roleMenuRef.current && !roleMenuRef.current.contains(event.target)) {
                setShowRoleMenu(false);
            }
            if (priorityMenuRef.current && !priorityMenuRef.current.contains(event.target)) {
                setShowPriorityMenu(false);
            }
            if (personMenuRef.current && !personMenuRef.current.contains(event.target)) {
                setShowPersonMenu(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            // Fermer le dropdown le plus "proche" ouvert (du plus interne au plus externe)
            if (showTypeMenu)     { event.stopPropagation(); setShowTypeMenu(false);     return; }
            if (showRoleMenu)     { event.stopPropagation(); setShowRoleMenu(false);     return; }
            if (showPriorityMenu) { event.stopPropagation(); setShowPriorityMenu(false); return; }
            if (showPersonMenu)   { event.stopPropagation(); setShowPersonMenu(false);   return; }
            if (showFilters)      { event.stopPropagation(); setShowFilters(false);      return; }
            if (showViewMenu)     { event.stopPropagation(); setShowViewMenu(false);     return; }
        };

        if (showViewMenu || showFilters || showTypeMenu || showRoleMenu || showPriorityMenu || showPersonMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown, true);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [showViewMenu, showFilters, showTypeMenu, showRoleMenu, showPriorityMenu, showPersonMenu]);

    const applyFilters = () => {
        setColumns(pendingColumns);
        onSearch('', type, role, person, priority);
        setShowFilters(false);
    };

    const resetFilters = () => {
        setType('Tous');
        setRole('Tous');
        setPriority('Tous');
        setPerson('Tous');
        setTypeInput('');
        setRoleInput('');
        setPriorityInput('');
        setPersonInput('');
        setPendingColumns(columns);
        setColumns(columns);
        onSearch('', 'Tous', 'Tous', 'Tous', 'Tous');
        setShowFilters(false);
        setShowAdvanced(false);
    };

    const handleViewModeChange = async (mode) => {
        const success = await setFavoritesViewMode(mode);

        if (success) {
            toast.success(`Vue changée en ${VIEW_MODES.find(v => v.value === mode)?.label}`, {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } else {
            toast.error('Erreur lors du changement de vue', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        }
        setShowViewMenu(false);
    };

    const currentViewMode = VIEW_MODES.find(vm => vm.value === (user?.favoritesViewMode || 'inline'));
    const CurrentIcon = currentViewMode?.icon || FiList;

    const hasActiveFilters = type !== 'Tous' || role !== 'Tous' || priority !== 'Tous' || person !== 'Tous';

    return (
        <div className="pt-4">
            <div className="flex gap-2 flex-wrap items-center">
                {/* Bouton de recherche qui ouvre la modale */}
                <button
                    type="button"
                    onClick={onSearchClick}
                    className="relative flex-grow min-w-[100px] sm:min-w-[200px] md:min-w-[300px] border border-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-md p-2 text-left hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors cursor-text"
                    data-tutorial="search-btn"
                >
                    <div className="flex items-center justify-between w-full">
                        <span className="text-gray-500 dark:text-gray-400">Rechercher...</span>
                        <div className="flex items-center gap-1">
                            <div className="hidden md:block">
                                <KeyboardShortcutBadge 
                                    shortcut={getSearchShortcut()} 
                                    icon={FiSearch} 
                                    variant="primary" 
                                    size="md"
                                />
                            </div>
                        </div>
                    </div>
                </button>

                {/* Bouton Filtres avec dropdown */}
                <div className="relative" ref={filtersRef}>
                    <button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`relative bg-gray-100 dark:bg-gray-700 text-black dark:text-white rounded-md p-[10px] px-3 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center gap-2 border border-primary dark:border-gray-600 ${hasActiveFilters ? 'ring-2 ring-accent' : ''
                            }`}
                        title="Filtres"
                    >
                        <FiFilter size={18} />
                        <span className="hidden sm:inline text-sm font-medium">Filtres</span>
                        {hasActiveFilters && (
                            <span className="absolute -top-1 -right-1 h-3 w-3 bg-accent rounded-full"></span>
                        )}
                    </button>

                    {showFilters && (
                        <div className="fixed sm:absolute left-4 right-4 sm:left-0 sm:right-auto mt-2 sm:w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 p-4 z-10 mx-[14px] sm:mx-0">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-black dark:text-white">Filtres</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowFilters(false)}
                                    className="text-gray-500 hover:text-black dark:hover:text-white"
                                >
                                    <FiX size={18} />
                                </button>
                            </div>

                            <div className="space-y-3">
                                <div className="relative" ref={typeMenuRef}>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Type
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={typeInput}
                                            onChange={e => {
                                                setTypeInput(e.target.value);
                                                setShowTypeMenu(true);
                                            }}
                                            onFocus={() => setShowTypeMenu(true)}
                                            onBlur={() => setTimeout(() => setShowTypeMenu(false), 200)}
                                            placeholder={type === 'Tous' ? 'Tous les types' : type}
                                            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md p-2 text-sm"
                                        />
                                        {showTypeMenu && (
                                            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-700 rounded-md shadow border dark:border-gray-700 z-20 max-h-48 overflow-y-auto dark:text-white text-black">
                                                <button
                                                    type="button"
                                                    tabIndex={0}
                                                    onClick={() => {
                                                        setType('Tous');
                                                        setTypeInput('');
                                                        setShowTypeMenu(false);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent/20 dark:hover:bg-accent/30 focus:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                    title="Tous les types"
                                                >
                                                    Tous les types
                                                </button>
                                                {types.filter(t => t.toLowerCase().includes(typeInput.toLowerCase())).map((t, idx) => (
                                                    <button
                                                        key={t}
                                                        type="button"
                                                        tabIndex={0}
                                                        onClick={() => {
                                                            setType(t);
                                                            setTypeInput(t);
                                                            setShowTypeMenu(false);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent/20 dark:hover:bg-accent/30 focus:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                        title={t}
                                                    >
                                                        {t}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="relative" ref={roleMenuRef}>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Rôle
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={roleInput}
                                            onChange={e => {
                                                setRoleInput(e.target.value);
                                                setShowRoleMenu(true);
                                            }}
                                            onFocus={() => setShowRoleMenu(true)}
                                            onBlur={() => setTimeout(() => setShowRoleMenu(false), 200)}
                                            placeholder={role === 'Tous' ? 'Tous les rôles' : role}
                                            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md p-2 text-sm"
                                        />
                                        {showRoleMenu && (
                                            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-700 rounded-md shadow border dark:border-gray-700 z-20 max-h-48 overflow-y-auto dark:text-white text-black">
                                                <button
                                                    type="button"
                                                    tabIndex={0}
                                                    onClick={() => {
                                                        setRole('Tous');
                                                        setRoleInput('');
                                                        setShowRoleMenu(false);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent/20 dark:hover:bg-accent/30 focus:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                    title="Tous les rôles"
                                                >
                                                    Tous les rôles
                                                </button>
                                                {roles.filter(r => r.toLowerCase().includes(roleInput.toLowerCase())).map((r, idx) => (
                                                    <button
                                                        key={r}
                                                        type="button"
                                                        tabIndex={0}
                                                        onClick={() => {
                                                            setRole(r);
                                                            setRoleInput(r);
                                                            setShowRoleMenu(false);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent/20 dark:hover:bg-accent/30 focus:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                        title={r}
                                                    >
                                                        {r}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="relative" ref={priorityMenuRef}>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Priorité
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={priorityInput}
                                            onChange={e => {
                                                setPriorityInput(e.target.value);
                                                setShowPriorityMenu(true);
                                            }}
                                            onFocus={() => setShowPriorityMenu(true)}
                                            onBlur={() => setTimeout(() => setShowPriorityMenu(false), 200)}
                                            placeholder={priority === 'Tous' ? 'Toutes les priorités' : priority}
                                            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md p-2 text-sm"
                                        />
                                        {showPriorityMenu && (
                                            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-700 rounded-md shadow border dark:border-gray-700 z-20 max-h-48 overflow-y-auto dark:text-white text-black">
                                                <button
                                                    type="button"
                                                    tabIndex={0}
                                                    onClick={() => {
                                                        setPriority('Tous');
                                                        setPriorityInput('');
                                                        setShowPriorityMenu(false);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent/20 dark:hover:bg-accent/30 focus:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                    title="Toutes les priorités"
                                                >
                                                    Toutes les priorités
                                                </button>
                                                {['critique', 'haute', 'moyenne', 'basse', 'minimale'].filter(p => p.toLowerCase().includes(priorityInput.toLowerCase())).map((p) => (
                                                    <button
                                                        key={p}
                                                        type="button"
                                                        tabIndex={0}
                                                        onClick={() => {
                                                            setPriority(p);
                                                            setPriorityInput(p);
                                                            setShowPriorityMenu(false);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent/20 dark:hover:bg-accent/30 focus:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                        title={p}
                                                    >
                                                        {p.charAt(0).toUpperCase() + p.slice(1)}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="relative" ref={personMenuRef}>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Créé par
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={personInput}
                                            onChange={e => {
                                                setPersonInput(e.target.value);
                                                setShowPersonMenu(true);
                                            }}
                                            onFocus={() => setShowPersonMenu(true)}
                                            onBlur={() => setTimeout(() => setShowPersonMenu(false), 200)}
                                            placeholder={person === 'Tous' ? 'Toutes les personnes' : person}
                                            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md p-2 text-sm"
                                        />
                                        {showPersonMenu && (
                                            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-700 rounded-md shadow border dark:border-gray-700 z-20 max-h-48 overflow-y-auto dark:text-white text-black">
                                                <button
                                                    type="button"
                                                    tabIndex={0}
                                                    onClick={() => {
                                                        setPerson('Tous');
                                                        setPersonInput('');
                                                        setShowPersonMenu(false);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent/20 dark:hover:bg-accent/30 focus:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                    title="Toutes les personnes"
                                                >
                                                    Toutes les personnes
                                                </button>
                                                {members.filter(m => m.name.toLowerCase().includes(personInput.toLowerCase())).map((m) => (
                                                    <button
                                                        key={m._id}
                                                        type="button"
                                                        tabIndex={0}
                                                        onClick={() => {
                                                            setPerson(m.name);
                                                            setPersonInput(m.name);
                                                            setShowPersonMenu(false);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent/20 dark:hover:bg-accent/30 focus:bg-accent/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                                        title={m.name}
                                                    >
                                                        {m.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {canManage && (
                                <div className="pt-2">
                                    {/* Modern advanced filters card */}
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowAdvanced(!showAdvanced); } }}
                                        aria-expanded={showAdvanced}
                                        className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                                    >
                                        <div className="flex items-center gap-3">
                                            <FiFilter className="text-gray-600 dark:text-gray-300" />
                                            <div>
                                                <div className="text-sm font-semibold text-gray-800 dark:text-white">Filtres avancés</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">Export et import</div>
                                            </div>
                                        </div>

                                        <FiChevronDown className={`text-gray-500 dark:text-gray-400 transition-transform duration-200 ${showAdvanced ? 'transform rotate-180' : ''}`} />
                                    </div>

                                    <div className={`mt-3 overflow-hidden transition-all duration-200 ${showAdvanced ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <div className="p-3 bg-white dark:bg-gray-800 rounded-md shadow-sm border dark:border-gray-700">
                                            <div className="flex flex-col gap-3">
                                                {/* Colonnes: moved to the "Vue" menu to keep top-bar filters focused */}

                                                {canManage && (
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Export / Import</label>
                                                        <div className="flex gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowImportModal(true)}
                                                                className="flex-1 flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                                                            >
                                                                <FiUpload />
                                                                <span>Importer</span>
                                                            </button>

                                                            <button
                                                                type="button"
                                                                onClick={() => setShowExportModal(true)}
                                                                className="flex-1 flex items-center justify-center gap-2 p-2 rounded-md text-sm font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                                                            >
                                                                <FiDownload />
                                                                <span>Exporter</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                )}

                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={resetFilters}
                                        className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md p-2 text-sm hover:bg-gray-300 dark:hover:bg-gray-500"
                                    >
                                        Réinitialiser
                                    </button>
                                    <button
                                        type="button"
                                        onClick={applyFilters}
                                        className="flex-1 bg-primary text-white dark:text-black rounded-md p-2 text-sm hover:bg-primary-hover"
                                    >
                                        Appliquer
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bouton Vue */}
                <div className="relative" ref={menuRef}>
                    <button
                        type="button"
                        onClick={() => setShowViewMenu(!showViewMenu)}
                        className="bg-gray-100 dark:bg-gray-700 text-black dark:text-white rounded-md p-[10px] px-3 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center gap-2 border border-primary dark:border-gray-600"
                        title={`Vue: ${currentViewMode?.label}`}
                    >
                        <CurrentIcon size={18} />
                        <span className="hidden sm:inline text-sm font-medium">Vue</span>
                    </button>

                    {showViewMenu && (
                        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 py-1 z-10">
                            
                            {/* Colonnes moved here from Filters */}
                            <div className="border-b dark:border-gray-700 py-2 pb-3 px-3 hidden sm:block">
                                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Colonnes</div>
                                <div className="flex gap-2">
                                    {COLUMN_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => { setPendingColumns(opt.value); setColumns(opt.value); }}
                                            className={`flex-1 p-2 rounded text-sm font-medium transition ${columns === opt.value
                                                ? 'bg-primary text-white dark:text-black hover:bg-primary-hover'
                                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {VIEW_MODES.map((mode) => {
                                const Icon = mode.icon;
                                const isActive = user?.favoritesViewMode === mode.value;
                                return (
                                    <button
                                        key={mode.value}
                                        type="button"
                                        onClick={() => handleViewModeChange(mode.value)}
                                        className={`w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 ${isActive ? 'bg-gray-50 dark:bg-gray-700' : ''
                                            }`}
                                    >
                                        <Icon className="h-5 w-5 mt-0.5 flex-shrink-0 text-black dark:text-white" />
                                        <div className="flex-1">
                                            <div className="text-black dark:text-white font-semibold text-sm">{mode.label}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{mode.description}</div>
                                        </div>
                                        {isActive && (
                                            <FiCheck className="h-6 w-4 text-green-600 dark:text-green-500 flex-shrink-0" />
                                        )}
                                    </button>
                                );
                            })}

                            {/* Bordure colorée */}
                            <div className="border-t dark:border-gray-700">
                                <button
                                    type="button"
                                    onClick={toggleColoredBorder}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                                >
                                    <FiSquare className="h-5 w-5 mt-0.5 flex-shrink-0 text-black dark:text-white" />
                                    <div className="flex-1">
                                        <div className="text-black dark:text-white font-semibold text-sm">Bordure colorée</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">Couleur du type sur la card</div>
                                    </div>
                                    {(user?.showColoredBorder ?? true) && (
                                        <FiCheck className="h-6 w-4 text-green-600 dark:text-green-500 flex-shrink-0" />
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bouton Valider (visible sur mobile) */}
                <button
                    type="button"
                    onClick={applyFilters}
                    className="flex-1 sm:hidden bg-primary font-semibold text-white dark:text-black rounded-md p-2 px-4 hover:bg-primary-hover"
                >
                    OK
                </button>
            </div>

            {/* Export / Import modals opened from the filters area to save top bar space */}
            <ExportModal
                open={showExportModal}
                onClose={() => setShowExportModal(false)}
                initialType="messages"
                onExportDone={() => {
                    setShowExportModal(false);
                    if (typeof onExportDone === 'function') {
                        try { onExportDone(); } catch (e) { /* ignore */ }
                    } else {
                        try { onSearch('', type, role); } catch (e) { /* ignore */ }
                    }
                }}
            />

            <ImportModal
                open={showImportModal}
                onClose={() => setShowImportModal(false)}
                defaultType="messages"
                onImportSuccess={async () => {
                    setShowImportModal(false);
                    if (typeof onImportSuccess === 'function') {
                        try { await onImportSuccess(); } catch (e) { /* ignore */ }
                    } else {
                        try { onSearch('', type, role); } catch (e) { /* ignore */ }
                    }
                }}
            />
        </div>
    );
};

export default MessageSearch;